import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { AgentSummary, Claim, CoveragePolicy, Scenario, ScenarioCatalogItem } from "@ti/core";
import { ArrowRight, Banknote, FileText, Gavel, Plus, ShieldOff, Umbrella } from "lucide-react";
import { useMemo } from "react";
import { AgentLink } from "@/components/domain/misc";
import { StatTile } from "@/components/domain/stat";
import { TierBadge } from "@/components/domain/status";
import { ErrorPanel } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { type ColumnDef, DataTable } from "@/components/ui/data-table";
import { EmptyState, Meter, Skeleton } from "@/components/ui/misc";
import { Tooltip } from "@/components/ui/tooltip";
import { MAGNITUDE_COLOR } from "@/features/agents/bits";
import { InsuredLimitChart } from "@/features/coverage/insured-limit-chart";
import { isLivePolicy, isOpenClaim, sumMoney } from "@/features/coverage/meta";
import { LossTypeChips, PolicyChip, PolicyStatusBadge, StateCounts, TermsBadge, daysRemaining, lossTypesOf, policyColors } from "@/features/coverage/policy-bits";
import { q } from "@/lib/api";
import { fmtDate, fmtInt, fmtMoney, fmtPct } from "@/lib/format";

export const Route = createFileRoute("/_app/coverage/")({
  component: PoliciesPage,
});

function PoliciesPage() {
  const policies = useQuery(q.policies());
  const scenarios = useQuery(q.scenarios());
  const claims = useQuery(q.claims());
  const agents = useQuery(q.agents());
  const catalog = useQuery(q.scenarioCatalog([]));

  const list = policies.data;
  const live = useMemo(() => (list ?? []).filter(isLivePolicy), [list]);
  const colors = useMemo(() => policyColors(list), [list]);
  const scenariosByPolicy = useMemo(() => groupBy(scenarios.data ?? [], (s) => s.policyId), [scenarios.data]);

  if (policies.error) return <ErrorPanel error={policies.error} reset={() => void policies.refetch()} />;

  return (
    <div className="flex flex-col gap-4">
      <Kpis policies={live} claims={claims.data} loading={policies.isPending} />

      <section aria-labelledby="policies-heading" className="flex flex-col gap-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 id="policies-heading" className="text-[13px] font-semibold">
              Policies
            </h2>
            <p className="mt-0.5 text-xs text-fg-3">One active policy per agent-scenario. Select a policy for its limits, scenarios, endorsements and claims.</p>
          </div>
        </div>
        {policies.isPending ? (
          <div className="grid gap-3 lg:grid-cols-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-[268px]" />
            ))}
          </div>
        ) : !list?.length ? (
          <Card>
            <EmptyState
              icon={<Umbrella />}
              title="No policies yet"
              description="Quote a policy over your agents: pick agents, choose scenarios from their catalog, set limits and terms, then bind."
              action={
                <Button size="sm" asChild className="mt-2">
                  <Link to="/coverage/quote">
                    <Plus /> New policy
                  </Link>
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {list.map((p) => (
              <PolicyCard key={p.id} policy={p} color={colors.get(p.id)} scenarios={scenariosByPolicy.get(p.id)} scenariosLoading={scenarios.isPending} agents={agents.data} />
            ))}
          </div>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="flex flex-col lg:col-span-5">
          <CardHeader
            title="Insured limit over time"
            description="Total aggregate limit across your policies. Each band is one policy; limits step up by endorsement as agents prove reliable."
            actions={
              <Link to="/coverage/endorsements" className="text-xs text-fg-3 hover:text-accent">
                Endorsements
              </Link>
            }
          />
          <CardBody className="flex-1 pb-3">{list ? <InsuredLimitChart policies={live} colors={colors} height={252} /> : <Skeleton className="h-[280px]" />}</CardBody>
        </Card>
        <Card className="flex flex-col lg:col-span-7">
          <CardHeader title="Coverage by agent" description="Which policies cover each agent, its insured limit, and scenarios in its catalog that no policy covers yet." />
          <AgentCoverageTable agents={agents.data} loading={agents.isPending} policies={list} colors={colors} scenarios={scenarios.data} catalog={catalog.data} />
        </Card>
      </div>
    </div>
  );
}

function groupBy<T>(items: T[], key: (t: T) => string) {
  const m = new Map<string, T[]>();
  for (const it of items) m.set(key(it), [...(m.get(key(it)) ?? []), it]);
  return m;
}

// ─── KPIs ────────────────────────────────────────────────────────────────────

function Kpis({ policies, claims, loading }: { policies: CoveragePolicy[]; claims: Claim[] | undefined; loading: boolean }) {
  const currency = policies[0]?.aggregateLimit.currency ?? "USD";
  const aggregate = sumMoney(policies, (p) => p.aggregateLimit.amount);
  const used = sumMoney(policies, (p) => p.used.amount);
  const annual = sumMoney(policies, (p) => p.premium.annual.amount);
  const monthly = sumMoney(policies, (p) => p.premium.monthly.amount);
  const active = policies.filter((p) => p.status === "active").length;
  const pending = policies.filter((p) => p.status === "pending_bind").length;
  const carriers = new Set(policies.map((p) => p.carrier)).size;
  const open = claims?.filter(isOpenClaim);
  const openLoss = sumMoney(open, (c) => c.loss.amount);
  const sk = (w: string) => <Skeleton className={`h-6 ${w}`} />;

  return (
    <section aria-label="Coverage summary" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatTile
        label="Active policies"
        icon={<FileText />}
        value={loading ? sk("w-10") : fmtInt(active)}
        sub={loading ? null : `${pending ? `${pending} pending bind · ` : ""}${carriers} ${carriers === 1 ? "carrier" : "carriers"}`}
      />
      <StatTile
        label="Total aggregate limit"
        icon={<Umbrella />}
        value={loading ? sk("w-24") : fmtMoney({ amount: aggregate, currency })}
        sub={loading ? null : `${fmtMoney({ amount: used, currency })} paid or reserved (${fmtPct(aggregate ? used / aggregate : 0, 1)})`}
        footer={loading ? null : <Meter value={used} max={aggregate || 1} tone={MAGNITUDE_COLOR} label="Aggregate limit used across policies" />}
      />
      <StatTile
        label="Annual premium"
        icon={<Banknote />}
        value={loading ? sk("w-20") : fmtMoney({ amount: annual, currency })}
        sub={loading ? null : `${fmtMoney({ amount: monthly, currency })} per month · ${fmtPct(aggregate ? annual / aggregate : 0, 2)} rate on line`}
      />
      <StatTile
        label="Open claims"
        icon={<Gavel />}
        href="/coverage/claims"
        value={open ? fmtInt(open.length) : sk("w-10")}
        sub={open ? `${fmtMoney({ amount: openLoss, currency })} in reported losses` : null}
      />
    </section>
  );
}

// ─── Policy card ─────────────────────────────────────────────────────────────

function PolicyCard({
  policy: p,
  color,
  scenarios,
  scenariosLoading,
  agents,
}: {
  policy: CoveragePolicy;
  color: string | undefined;
  scenarios: Scenario[] | undefined;
  scenariosLoading: boolean;
  agents: AgentSummary[] | undefined;
}) {
  const usedShare = p.aggregateLimit.amount ? p.used.amount / p.aggregateLimit.amount : 0;
  const left = daysRemaining(p);
  const agentName = (id: string) => agents?.find((a) => a.id === id)?.name ?? id;
  const lossTypes = lossTypesOf(scenarios ?? []);

  return (
    <Card className="group relative flex flex-col transition-colors hover:border-border-strong focus-within:border-border-strong">
      <div className="flex items-start justify-between gap-3 px-4 pt-3.5">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-[14px] font-semibold text-fg">
            <span className="size-2.5 shrink-0 rounded-sm" style={{ background: color }} aria-hidden />
            {/* Stretched link: the whole card is the hit target; inner links sit above it. */}
            <Link
              to="/coverage/policies/$policyId"
              params={{ policyId: p.id }}
              className="truncate outline-none after:absolute after:inset-0 after:rounded-lg focus-visible:after:ring-2 focus-visible:after:ring-ring"
            >
              {p.name}
            </Link>
          </h3>
          <p className="mt-0.5 truncate text-xs text-fg-3">
            {p.carrier} · <span className="font-mono text-[11.5px]">{p.policyNumber}</span>
          </p>
        </div>
        <div className="relative z-[1] flex shrink-0 items-center gap-1.5">
          <TermsBadge mode={p.mode} />
          <PolicyStatusBadge status={p.status} />
        </div>
      </div>

      <div className="relative z-[1] mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 px-4">
        <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Covers</span>
        {scenariosLoading ? <Skeleton className="h-4 w-40" /> : <LossTypeChips types={lossTypesOf(scenarios ?? [])} />}
        {!scenariosLoading && lossTypes.length === 1 ? (
          <span className="text-xs text-fg-3">{lossTypes[0] === "first_party" ? "your own losses only" : "losses of people your agents serve"}</span>
        ) : null}
      </div>

      <div className="relative z-[1] mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pointer-events-none [&_a]:pointer-events-auto">
        <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Agents</span>
        {p.agentIds.map((id) => (
          <AgentLink key={id} id={id} name={agentName(id)} className="text-[12px]" />
        ))}
      </div>

      <div className="mt-3 px-4">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <span className="text-xl font-semibold tracking-tight tabular">{fmtMoney(p.aggregateLimit)}</span>
            <span className="ml-1.5 text-xs text-fg-3">aggregate</span>
          </div>
          <span className="text-xs text-fg-3 tabular">
            <span className="text-fg-2">{fmtMoney(p.used)}</span> used · {fmtPct(usedShare, 1)}
          </span>
        </div>
        <Meter value={p.used.amount} max={p.aggregateLimit.amount || 1} tone={MAGNITUDE_COLOR} className="mt-1.5" label={`${p.name}: aggregate limit used`} />
      </div>

      <dl className="mt-3 grid grid-cols-4 gap-3 px-4 text-[13px]">
        <Mini label="Per claim" value={fmtMoney(p.perClaimLimit)} />
        <Mini label="Deductible" value={fmtMoney(p.deductible)} />
        <Mini label="Premium" value={`${fmtMoney(p.premium.annual)}/yr`} sub={`${fmtMoney(p.premium.monthly)}/mo`} />
        <Mini label="Period" value={p.status === "expired" ? "Ended" : `${left} days left`} sub={`to ${fmtDate(p.periodEnd)}`} />
      </dl>

      <div className="relative z-[1] mt-3 flex flex-wrap items-center justify-between gap-2 border-t bg-surface-2/40 px-4 py-2.5">
        {scenariosLoading ? <Skeleton className="h-5 w-56" /> : <StateCounts scenarios={scenarios ?? []} />}
        <div className="flex items-center gap-3 text-xs">
          <span className="text-fg-3 tabular">
            {p.scenarioCount} {p.scenarioCount === 1 ? "scenario" : "scenarios"}
          </span>
          {p.openClaims > 0 ? (
            <Link to="/coverage/claims" search={{ policyId: p.id }} className="inline-flex items-center gap-1 text-fg-2 hover:text-accent hover:underline">
              <Gavel className="size-3.5 text-fg-3" aria-hidden />
              <span className="tabular">{p.openClaims}</span> open {p.openClaims === 1 ? "claim" : "claims"}
            </Link>
          ) : (
            <span className="text-fg-3">No open claims</span>
          )}
        </div>
      </div>
    </Card>
  );
}

function Mini({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-fg-3">{label}</dt>
      <dd className="mt-0.5 truncate font-medium tabular text-fg">{value}</dd>
      {sub ? <dd className="truncate text-[11px] text-fg-3 tabular">{sub}</dd> : null}
    </div>
  );
}

// ─── Coverage by agent ───────────────────────────────────────────────────────

interface AgentRow {
  agent: AgentSummary;
  policies: CoveragePolicy[];
  covered: number;
  available: ScenarioCatalogItem[];
  ineligible: number;
}

function AgentCoverageTable({
  agents,
  loading,
  policies,
  colors,
  scenarios,
  catalog,
}: {
  agents: AgentSummary[] | undefined;
  loading: boolean;
  policies: CoveragePolicy[] | undefined;
  colors: Map<string, string>;
  scenarios: Scenario[] | undefined;
  catalog: ScenarioCatalogItem[] | undefined;
}) {
  const rows = useMemo<AgentRow[] | undefined>(() => {
    if (!agents) return undefined;
    const live = new Set((policies ?? []).filter(isLivePolicy).map((p) => p.id));
    return agents.map((a) => {
      const items = (catalog ?? []).filter((c) => c.agentId === a.id);
      return {
        agent: a,
        policies: (policies ?? []).filter((p) => a.policyIds.includes(p.id)),
        covered: (scenarios ?? []).filter((s) => s.agentId === a.id && live.has(s.policyId) && s.subLimit.amount > 0).length,
        available: items.filter((c) => c.eligible && !c.coveredByPolicyId),
        ineligible: items.filter((c) => !c.eligible).length,
      };
    });
  }, [agents, policies, scenarios, catalog]);

  const columns = useMemo<ColumnDef<AgentRow>[]>(
    () => [
      {
        id: "agent",
        header: "Agent",
        accessorFn: (r) => r.agent.name,
        cell: ({ row: { original: r } }) => (
          <div className="flex flex-col items-start gap-0.5">
            <AgentLink id={r.agent.id} name={r.agent.name} className="whitespace-nowrap" />
            <span className="flex items-center gap-1.5 text-[11px] text-fg-3">
              <TierBadge tier={r.agent.tier} showLabel={false} />
              ARS {r.agent.ars}
            </span>
          </div>
        ),
      },
      {
        id: "policies",
        header: "Covered by",
        accessorFn: (r) => r.policies.length,
        cell: ({ row: { original: r } }) =>
          r.policies.length ? (
            <div className="flex max-w-[220px] flex-col items-start gap-1">
              {r.policies.map((p) => (
                <PolicyChip key={p.id} id={p.id} name={p.name} color={colors.get(p.id)} />
              ))}
            </div>
          ) : (
            <Tooltip content="No policy covers any scenario of this agent. Losses it causes are not insured.">
              <Badge tone="warning" tabIndex={0}>
                <ShieldOff /> Uninsured
              </Badge>
            </Tooltip>
          ),
      },
      {
        id: "limit",
        header: "Insured limit",
        accessorFn: (r) => r.agent.insuredLimit.amount,
        meta: { align: "right" },
        cell: ({ row: { original: r } }) => (
          <div className="flex flex-col items-end">
            <span className={r.agent.insuredLimit.amount ? "font-medium" : "text-fg-3"}>{fmtMoney(r.agent.insuredLimit)}</span>
            <span className="text-[11px] text-fg-3">
              {r.covered} {r.covered === 1 ? "scenario" : "scenarios"}
            </span>
          </div>
        ),
      },
      {
        id: "gap",
        header: "Not yet insured",
        accessorFn: (r) => r.available.length,
        meta: { align: "right" },
        cell: ({ row: { original: r } }) =>
          r.available.length ? (
            <div className="flex flex-col items-end gap-1">
              <Button variant="outline" size="sm" asChild>
                <Link to="/coverage/quote" search={{ agents: r.agent.id }} title={r.available.map((c) => c.label).join(", ")}>
                  Insure {r.available.length} more <ArrowRight />
                </Link>
              </Button>
              {r.ineligible ? <span className="text-[11px] text-fg-3">+{r.ineligible} not eligible yet</span> : null}
            </div>
          ) : (
            <span className="text-xs text-fg-3">{r.ineligible ? `${r.ineligible} not eligible yet` : "Fully insured"}</span>
          ),
      },
    ],
    [colors],
  );

  return <DataTable data={rows} loading={loading} columns={columns} getRowId={(r) => r.agent.id} dense empty={{ title: "No agents", description: "Connect an agent to start insuring it." }} />;
}
